<template>
  <section v-if="eligible" class="person-video-generator">
    <div class="person-video-actions">
      <b-button
        class="person-video-generate"
        variant="outline-primary"
        :disabled="activeJob || !validOptions"
        @click="generate"
      >
        <b-spinner v-if="ownJobActive" small />
        {{ ownJobActive ? $t('people_video_generating') : $t('people_video_generate') }}
      </b-button>
      <b-button
        v-if="ownJobActive"
        class="person-video-cancel"
        variant="outline-light"
        @click="$emit('cancel')"
      >
        {{ $t('people_video_cancel') }}
      </b-button>
      <b-button
        v-if="ownJob && ownJob.status === 'completed' && ownJob.download_ready"
        class="person-video-download"
        variant="primary"
        :href="downloadUrl"
        download
      >
        <i class="bi bi-download" /> {{ $t('people_video_download') }}
      </b-button>
      <b-button
        class="person-video-options-toggle"
        variant="link"
        @click="advanced = !advanced"
      >
        {{ advanced ? $t('people_video_hide_options') : $t('people_video_options') }}
      </b-button>
    </div>

    <b-progress
      v-if="ownJobActive"
      class="person-video-progress"
      :value="ownJob.progress"
      :max="100"
      show-progress
      animated
    />
    <small v-if="otherJobActive" class="person-video-busy">
      {{ $t('people_video_busy') }}
    </small>
    <b-alert v-if="ownJob && ownJob.status === 'failed'" show variant="danger">
      {{ ownJob.error || $t('people_video_failed') }}
    </b-alert>

    <div v-if="advanced" class="person-video-options">
      <label>
        <span>{{ $t('people_video_duration') }}</span>
        <b-input v-model.number="durationSeconds" type="number" min="5" max="60" />
      </label>
      <label>
        <span>{{ $t('people_video_images') }}</span>
        <b-input v-model.number="imageCount" type="number" min="1" max="120" />
      </label>
      <label>
        <span>{{ $t('people_video_dissolve') }}</span>
        <b-input v-model.number="dissolveSeconds" type="number" min="0.05" max="2" step="0.05" />
      </label>
      <label>
        <span>{{ $t('people_video_easing') }}</span>
        <b-select v-model="easeMode">
          <option value="linear">{{ $t('people_video_easing_linear') }}</option>
          <option value="smoothstep">{{ $t('people_video_easing_smoothstep') }}</option>
        </b-select>
      </label>
    </div>
  </section>
</template>

<script>
export default {
  props: {
    person: { type: Object, required: true },
    job: { type: Object, default: null },
    downloadUrl: { type: String, default: '' },
  },
  data() {
    return {
      advanced: false,
      durationSeconds: 30,
      imageCount: 20,
      dissolveSeconds: 0.4,
      easeMode: 'linear',
    }
  },
  computed: {
    eligible() {
      return this.person.status === 'confirmed'
        || (this.person.status === 'candidate' && Boolean(this.person.name?.trim()))
    },
    activeJob() {
      return ['pending', 'running'].includes(this.job?.status)
    },
    ownJob() {
      return this.job?.person_id === this.person.id ? this.job : null
    },
    ownJobActive() {
      return Boolean(this.ownJob && ['pending', 'running'].includes(this.ownJob.status))
    },
    otherJobActive() {
      return Boolean(this.activeJob && !this.ownJob)
    },
    validOptions() {
      return Number.isFinite(this.durationSeconds)
        && this.durationSeconds >= 5
        && this.durationSeconds <= 60
        && Number.isInteger(this.imageCount)
        && this.imageCount >= 1
        && this.imageCount <= 120
        && Number.isFinite(this.dissolveSeconds)
        && this.dissolveSeconds >= 0.05
        && this.dissolveSeconds <= 2
        && this.dissolveSeconds < this.durationSeconds
        && ['linear', 'smoothstep'].includes(this.easeMode)
    },
  },
  methods: {
    generate() {
      if (!this.validOptions || this.activeJob) return
      this.$emit('generate', {
        duration_seconds: this.durationSeconds,
        image_count: this.imageCount,
        dissolve_seconds: this.dissolveSeconds,
        ease_mode: this.easeMode,
      })
    },
  },
}
</script>

<style scoped>
.person-video-generator {
  flex: 0 0 auto;
  margin-bottom: 1rem;
  padding: .75rem;
  border: 1px solid rgba(127, 127, 127, .25);
  border-radius: 8px;
  background: rgba(127, 127, 127, .06);
}

.person-video-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .5rem;
}

.person-video-options-toggle {
  margin-left: auto;
}

.person-video-progress,
.person-video-busy,
.person-video-generator .alert {
  display: block;
  margin-top: .75rem;
}

.person-video-options {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: .75rem;
  margin-top: .75rem;
}

.person-video-options label {
  margin: 0;
}

.person-video-options span {
  display: block;
  margin-bottom: .25rem;
  color: var(--ghosted-text-color);
  font-size: .75rem;
}

@media (max-width: 850px) {
  .person-video-options {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }
}
</style>
